import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from, NEVER } from 'rxjs';
import { catchError, filter, take, switchMap, timeout } from 'rxjs/operators';
import { AuthService } from '../components/framework/services/auth.service';
import { ErrorUtilitiesService } from '../components/framework/errorhandling/error-utilities.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private readonly pendingRequests = new Set<string>();

  constructor(
    private readonly authService: AuthService,
    private readonly errorUtilities: ErrorUtilitiesService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.addAuthHeader(req)).pipe(
      switchMap(authReq => next.handle(authReq)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        // Extract user-friendly error message and include it in the error
        const userMessage = this.errorUtilities.extractErrorMessage(error);
        const enhancedError = error instanceof HttpErrorResponse
          ? new HttpErrorResponse({
              error: error.error,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url || undefined
            })
          : error;
        // Add message property for easy access
        (enhancedError as any).message = userMessage;
        return throwError(() => enhancedError);
      })
    );
  }

  private async addAuthHeader(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    const requestUrl = req.url.length > 80 ? req.url.substring(0, 80) + '...' : req.url;
    // console.log(`🔍 [HTTP Interceptor] Preparing request to: ${requestUrl}`);

    try {
      // First check if token is expired or expiring soon
      const isExpiring = await this.authService.isTokenExpiredOrExpiring();

      if (isExpiring) {
        // console.log('🔄 [HTTP Interceptor] Token expired or expiring - attempting proactive renewal before API call');

        try {
          // Try to renew the token before making the request
          const renewedUser = await this.authService.renewToken();

          if (renewedUser?.access_token) {
            // console.log('✅ [HTTP Interceptor] Proactive token renewal successful - using new token for request');
            return req.clone({
              setHeaders: {
                Authorization: `Bearer ${renewedUser.access_token}`
              }
            });
          } else {
            console.warn('⚠️ [HTTP Interceptor] Proactive renewal returned no token - proceeding without auth header');
            // Let the request proceed - the 401 handler will catch it
            return req;
          }
        } catch (renewError) {
          console.error('❌ [HTTP Interceptor] Proactive token renewal failed:', renewError);

          // Handle idle timeout and expired token scenarios
          const errorMessage = renewError instanceof Error ? renewError.message : String(renewError);
          if (errorMessage?.includes('user inactive') || errorMessage?.includes('inactivity') || errorMessage?.includes('expired')) {
            console.log('🚪 [HTTP Interceptor] Session expired due to inactivity or token expiration - user redirected to login');
            // Don't call logout() here as the auth service already handled the redirect
            throw new Error('Session expired. Please sign in again.');
          }

          // For other renewal failures, proceed with request and let 401 handler deal with it
          return req;
        }
      }

      // Token is valid, get it and attach to request
      const token = await this.authService.getAccessToken();
      if (token) {
        // console.log('✅ [HTTP Interceptor] Using existing valid token for request');
        return req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      console.warn('⚠️ [HTTP Interceptor] No token available - proceeding without authorization header');
      return req;
    } catch (error) {
      console.error('❌ [HTTP Interceptor] Error in addAuthHeader:', error);
      return req;
    }
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestUrl = req.url.length > 80 ? req.url.substring(0, 80) + '...' : req.url;
    console.error(`🚨 [HTTP Interceptor] 401 Unauthorized received from: ${requestUrl}`);

    // Create a unique request ID to prevent duplicate processing
    const requestId = `${req.method}_${req.url}_${Date.now()}`;

    if (this.pendingRequests.has(requestId)) {
      // console.log('🔄 [HTTP Interceptor] Duplicate 401 request blocked:', requestId);
      return throwError(() => ({
        status: 401,
        authenticationRequired: true,
        message: 'Duplicate authentication request blocked'
      }));
    }

    this.pendingRequests.add(requestId);

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // console.log('🔄 [HTTP Interceptor] Starting token renewal for 401 error');

      return from(this.authService.renewToken()).pipe(
        switchMap((user) => {
          this.isRefreshing = false;
          this.pendingRequests.delete(requestId);

          if (user?.access_token) {
            // console.log('✅ [HTTP Interceptor] Token renewal successful after 401, retrying original request');
            this.refreshTokenSubject.next(user.access_token);
            return from(this.addAuthHeader(req)).pipe(
              switchMap(authReq => next.handle(authReq))
            );
          } else {
            console.warn('❌ [HTTP Interceptor] Token renewal returned no user');
            return throwError(() => ({
              status: 401,
              authenticationRequired: true,
              message: 'Please sign in to continue'
            }));
          }
        }),
        catchError((renewalError) => {
          this.isRefreshing = false;
          this.pendingRequests.delete(requestId);

          console.error('❌ [HTTP Interceptor] Token renewal failed after 401:', renewalError);

          // Check if it's a cooldown error
          if (renewalError.message?.includes('cooldown')) {
            console.warn('⏰ [HTTP Interceptor] Token renewal in cooldown period');
            return throwError(() => ({
              status: 429, // Too Many Requests
              authenticationRequired: false,
              message: 'Token renewal temporarily unavailable. Please try again shortly.',
              originalError: renewalError
            }));
          }

          // Extract the actual error message if it exists
          let errorMessage = 'Authentication session expired. Please sign in to continue.';
          const exceptionMessage = renewalError?.error?.responseException?.exceptionMessage
            || renewalError?.originalError?.error?.responseException?.exceptionMessage;

          if (exceptionMessage) {
            errorMessage = exceptionMessage;
          } else if (renewalError?.message && !renewalError.message.includes('Http failure')) {
            errorMessage = renewalError.message;
          }

          // For other renewal failures, indicate auth is needed
          return throwError(() => ({
            status: 401,
            authenticationRequired: true,
            message: errorMessage,
            originalError: renewalError
          }));
        })
      );
    } else {
      // console.log('🔄 [HTTP Interceptor] Token renewal already in progress, waiting for completion...');
      // If we're already refreshing, wait for the new token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(() => {
          this.pendingRequests.delete(requestId);
          return from(this.addAuthHeader(req));
        }),
        switchMap(authReq => next.handle(authReq)),
        catchError(() => {
          this.pendingRequests.delete(requestId);
          return throwError(() => ({
            status: 401,
            authenticationRequired: true,
            message: 'Authentication required'
          }));
        })
      );
    }
  }
}
